import { Injectable } from "@angular/core";
import { HttpService } from "./http.service";
import { environment } from "@environments/environment";
import { Observable, map } from "rxjs";
import { SessionService } from "./session.service";

export enum DomainStatus {
  Pending = 'Pending',
  Completed = 'Completed',
  Failed = 'Failed',
}

export interface BrandingConfig {
    id: number;
    userId: number;
    lightLogo?: string; // URL for display
    darkLogo?: string; // URL for display
    lightLogoId?: number | null;
    darkLogoId?: number | null;
    companyName: string;
    companyDescription: string;
    brandingColorPalette: BrandingColorPalette;
    favicon?: string; // URL for display
    faviconId?: number | null;
    customCss: string;
    domain?: string;
    domainStatus?: string;
    smtp?: {
        host: string;
        port: number;
        secure: boolean;
        auth: {
            user: string;
            pass: string;
        };
    };
}

export interface BrandingColorPalette {
    primaryColor: string;
    secondaryColor: string;
}

export interface UpdateBrandingDto {
    companyName: string;
    companyDescription: string;
    brandingColorPalette: BrandingColorPalette | string; // Can be object or JSON string
    faviconId?: number | null;
    lightLogoId?: number | null;
    darkLogoId?: number | null;
    customCss?: string;
    domain?: string;
    domainStatus?: string;
    smtp?: {
        host: string;
        port: number;
        secure: boolean;
        auth: {
            user: string;
            pass: string;
        };
    } | string; // Can be object or JSON string
}

export interface BrandingRequest {
    type: 'domain' | 'workspace';
    identifier: string; // domain name or user ID
}

@Injectable({
    providedIn: 'root'
})
export class BrandingService {

    constructor(
        private http: HttpService,
        private sessionSvc: SessionService
    ) {}

    /**
     * Get branding configuration by domain or user ID
     */
    getBrandingConfig(request: BrandingRequest): Observable<BrandingConfig> {
        const endpoint = request.type === 'domain' 
            ? `${environment.apiUrl}/public/branding`
            : `${environment.apiUrl}/${this.sessionSvc.getUserWorkspace()?.uid}/workspace-user/branding`;
            
        return this.http.get(endpoint)
            .pipe(map((data: any) => {
                return data.data;
            }));
    }

    getBrandingByUser(): Observable<BrandingConfig> {
        return this.http.get(`${environment.apiUrl}/branding`)
            .pipe(map((data: any) => {
                return data.data;
            }));
    }

    /**
     * Get branding configuration for the current domain
     */
    getBrandingConfigByDomain(): Observable<BrandingConfig> {
        const domain = window.location.hostname;
        return this.getBrandingConfig({ type: 'domain', identifier: domain });
    }

    /**
     * Get branding configuration for the current user
     */
    getBrandingConfigByWorkspace(userId: string): Observable<BrandingConfig> {
        return this.getBrandingConfig({ type: 'workspace', identifier: userId });
    }

    /**
     * Update branding configuration with multipart form data
     */
    updateBrandingConfig(formData: FormData): Observable<BrandingConfig> {
        return this.http.patch(`${environment.apiUrl}/branding`, formData)
            .pipe(map((data: any) => {
                return data.data;
            }));
    }

    /**
     * Update branding configuration with DTO and files (matches backend structure)
     */
    updateBrandingConfigWithFiles(updateBrandingDto: UpdateBrandingDto, filesFormData: FormData): Observable<BrandingConfig> {
        // Create FormData with DTO fields as individual form fields and files
        const combinedFormData = new FormData();
        
        // Add DTO fields as individual form fields
        combinedFormData.append('companyName', updateBrandingDto.companyName);
        combinedFormData.append('companyDescription', updateBrandingDto.companyDescription);
        combinedFormData.append('brandingColorPalette', JSON.stringify(updateBrandingDto.brandingColorPalette));
        
        // Add file IDs if they exist
        if (updateBrandingDto.faviconId !== undefined) {
            combinedFormData.append('faviconId', String(updateBrandingDto.faviconId));
        }
        if (updateBrandingDto.lightLogoId !== undefined) {
            combinedFormData.append('lightLogoId', String(updateBrandingDto.lightLogoId));
        }
        if (updateBrandingDto.darkLogoId !== undefined) {
            combinedFormData.append('darkLogoId', String(updateBrandingDto.darkLogoId));
        }
        if (updateBrandingDto.customCss) {
            combinedFormData.append('customCss', updateBrandingDto.customCss);
        }
        if (updateBrandingDto.domain) {
            combinedFormData.append('domain', updateBrandingDto.domain);
        }
        if (updateBrandingDto.domainStatus) {
            combinedFormData.append('domainStatus', updateBrandingDto.domainStatus);
        }
        if (updateBrandingDto.smtp) {
            combinedFormData.append('smtp', JSON.stringify(updateBrandingDto.smtp));
        }
        
        // Add files if they exist
        if (filesFormData.has('lightLogo')) {
            const lightLogoFile = filesFormData.get('lightLogo') as File;
            combinedFormData.append('lightLogo', lightLogoFile);
        }
        if (filesFormData.has('darkLogo')) {
            const darkLogoFile = filesFormData.get('darkLogo') as File;
            combinedFormData.append('darkLogo', darkLogoFile);
        }
        if (filesFormData.has('favicon')) {
            const faviconFile = filesFormData.get('favicon') as File;
            combinedFormData.append('favicon', faviconFile);
        }
        
        return this.http.patch(`${environment.apiUrl}/branding`, combinedFormData)
            .pipe(map((data: any) => {
                return data.data;
            }));
    }

    /**
     * Update branding configuration with DTO (uses file IDs)
     * Automatically stringifies brandingColorPalette and smtp if they're objects
     */
    updateBrandingConfigDto(updateBrandingDto: UpdateBrandingDto): Observable<BrandingConfig> {
        // Transform DTO to match backend expectations
        const transformedDto = {
            ...updateBrandingDto,
            brandingColorPalette: typeof updateBrandingDto.brandingColorPalette === 'string' 
                ? updateBrandingDto.brandingColorPalette 
                : JSON.stringify(updateBrandingDto.brandingColorPalette),
            smtp: typeof updateBrandingDto.smtp === 'string'
                ? updateBrandingDto.smtp
                : updateBrandingDto.smtp ? JSON.stringify(updateBrandingDto.smtp) : undefined
        };

        console.log('Transformed DTO (branding service):', JSON.stringify(transformedDto, null, 2));
        console.log('File IDs in transformed - Light:', transformedDto.lightLogoId, 'Dark:', transformedDto.darkLogoId, 'Favicon:', transformedDto.faviconId);

        return this.http.patch(`${environment.apiUrl}/branding`, transformedDto)
            .pipe(map((data: any) => {
                return data.data;
            }));
    }

    /**
     * Remove a logo by setting its ID to null
     */
    removeLogo(type: 'light' | 'dark'): Observable<BrandingConfig> {
        const dto: Partial<UpdateBrandingDto> = {};
        if (type === 'light') {
            dto.lightLogoId = null;
        } else {
            dto.darkLogoId = null;
        }
        return this.updateBrandingConfigDto(dto as UpdateBrandingDto);
    }

    /**
     * Remove favicon by setting its ID to null
     */
    removeFavicon(): Observable<BrandingConfig> {
        const dto: Partial<UpdateBrandingDto> = {
            faviconId: null
        };
        return this.updateBrandingConfigDto(dto as UpdateBrandingDto);
    }

    /**
     * Upload logo
     */
    uploadLogo(file: File): Observable<{ logoUrl: string }> {
        const formData = new FormData();
        formData.append('logo', file);
        
        return this.http.post(`${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/agency/branding/logo`, formData)
            .pipe(map((data: any) => {
                return data.data;
            }));
    }

    /**
     * Upload favicon
     */
    uploadFavicon(file: File): Observable<{ faviconUrl: string }> {
        const formData = new FormData();
        formData.append('favicon', file);
        
        return this.http.post(`${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/agency/branding/favicon`, formData)
            .pipe(map((data: any) => {
                return data.data;
            }));
    }

    /**
     * Verify custom domain
     */
    verifyDomain(domain: string): Observable<{ valid: boolean; message?: string }> {
        return this.http.post(`${environment.apiUrl}/branding/checkDomain`, { domain })
            .pipe(map((data: any) => {
                return data.data;
            }));
    }

    /**
     * Test SMTP connection
     */
    testSmtpConnection(smtpConfig: any): Observable<{ success: boolean; message?: string }> {
        return this.http.post(`${environment.apiUrl}/branding/verifySmtp`, smtpConfig)
            .pipe(map((data: any) => {
                return data.data;
            }));
    }

    /**
     * Reset branding to default
     */
    resetBranding(): Observable<BrandingConfig> {
        return this.http.post(`${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/agency/branding/reset`, {})
            .pipe(map((data: any) => {
                return data.data;
            }));
    }
}
